import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

/**
 * Root-level service for grid component action notifications.
 *
 * Provides a lightweight pub-sub mechanism for grid-related UI events
 * (e.g., delete clicks, row selections) that need to coordinate between
 * components without going through NgRx.
 *
 * This complements (not replaces) NgRx actions - components should still
 * dispatch actions for state management, but can use this service for
 * direct component coordination.
 *
 * **Why This Service Exists**:
 * Components with component-level providers (e.g., provideComponentStore)
 * create isolated injector scopes that cannot access NgRx Actions in
 * production builds due to tree-shaking. This service provides a pure
 * RxJS alternative that bypasses Angular DI hierarchy issues.
 *
 * **Usage Pattern**:
 * - DeleteItemIconComponent: Calls notifyDeleteRow() when icon clicked
 * - CdwSearchGridComponent: Subscribes to deleteRow$ filtered by gridComponentId
 *
 * **Multi-Instance Support**:
 * The optional gridComponentId allows multiple grid instances on the same
 * page to filter notifications, ensuring each grid only responds to its
 * own row delete events.
 *
 * @example
 * ```typescript
 * // In DeleteItemIconComponent:
 * this.gridNotifications.notifyDeleteRow(this.rowId, this.gridComponentId);
 *
 * // In CdwSearchGridComponent:
 * this.gridNotifications.deleteRow$
 *   .pipe(
 *     filter(({ gridComponentId }) => gridComponentId === this.componentId),
 *     takeUntil(this.onDestroy$)
 *   )
 *   .subscribe(({ rowId }) => {
 *     this.deleteRowClicked.emit({ rowId, gridComponentId: this.componentId });
 *   });
 * ```
 */
@Injectable({ providedIn: 'root' })
export class GridActionNotificationService {
  /**
   * Private Subject for delete row notifications.
   * Kept private to enforce one-way data flow through public methods.
   */
  private readonly deleteRowSubject = new Subject<{
    rowId: number | string;
    gridComponentId?: string;
  }>();

  /**
   * Observable stream of delete row notifications.
   *
   * Components should filter this stream by gridComponentId to handle
   * only their own delete events in multi-instance scenarios.
   *
   * **Important**: Always use takeUntil() to prevent memory leaks when
   * subscribing in component lifecycle methods.
   */
  public readonly deleteRow$: Observable<{
    rowId: number | string;
    gridComponentId?: string;
  }> = this.deleteRowSubject.asObservable();

  /**
   * Notify that a delete icon was clicked in a grid row.
   *
   * This method should be called by DeleteItemIconComponent when the
   * delete icon is clicked. It broadcasts the notification to all
   * subscribers of deleteRow$.
   *
   * **Best Practice**: Always call this in parallel with the NgRx
   * action dispatch to maintain both state management and component
   * coordination paths.
   *
   * @param rowId The ID of the row where delete was clicked
   * @param gridComponentId Optional grid instance identifier for filtering
   *                        in multi-grid scenarios. Should match the
   *                        componentId property of the target grid.
   *
   * @example
   * ```typescript
   * // Dispatch both NgRx action and Subject notification:
   * this.store.dispatch(deleteRowClicked({ rowId, gridComponentId }));
   * this.gridNotifications.notifyDeleteRow(rowId, gridComponentId);
   * ```
   */
  public notifyDeleteRow(rowId: number | string, gridComponentId?: string): void {
    this.deleteRowSubject.next({ rowId, gridComponentId });
  }
}
